/**
 * Auth Slice for Wadakaroo
 *
 * Manages authentication state including:
 * - Login / Logout
 * - Email registration (creates user account)
 * - Password reset (forced on first login / admin-triggered reset)
 * - Session initialization from stored tokens
 * - User data from JWT claims
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    setTokens, clearAuth, parseToken, setUser,
    getAccessToken, getRefreshToken, isTokenExpired, getUser,
    broadcastLogout,
} from '../../services/authService';
import { loginApi, registerUserApi, logoutApi, resetPasswordApi } from '../../services/api';

// ============================================
// Async Thunks
// ============================================

/**
 * Login with email and password
 */
export const loginUser = createAsyncThunk(
    'auth/loginUser',
    async ({ email, password }, { rejectWithValue }) => {
        try {
            const data = await loginApi(email, password);
            const { accessToken, refreshToken } = data;

            // Store tokens
            setTokens(accessToken, refreshToken);

            // Parse user from token
            const user = parseToken(accessToken);
            if (user) {
                setUser(user);
            }

            // API response may also include organisationSetupRequired flag
            // Use it to override if present (false means org IS provisioned)
            const orgSetupRequired = data.organisationSetupRequired;
            const organisationProvisioned = orgSetupRequired === false || user?.organisationProvisioned;
            const registrationComplete = user?.registrationComplete || organisationProvisioned;

            // Backend signals forced reset via passwordResetRequired on both the
            // JWT claims and the top-level response. Either source is authoritative.
            const passwordResetRequired = data.passwordResetRequired === true
                || user?.passwordResetRequired === true;

            return {
                user: {
                    ...user,
                    registrationComplete,
                    organisationProvisioned,
                    passwordResetRequired,
                },
                accessToken,
                refreshToken,
                passwordResetRequired,
                passwordResetReason: data.passwordResetReason || user?.passwordResetReason || null,
                passwordResetMessage: data.passwordResetMessage || null,
            };
        } catch (error) {
            // Distinguish "the server said no" from "we never reached a server".
            //
            // On a network failure axios leaves error.response UNDEFINED, so the old
            // code fell straight through to the credentials message — reporting a
            // misconfigured API base URL, a DNS failure or a dead backend as a wrong
            // password. That cost real debugging time: the UI said "check your
            // credentials" while the network tab showed ERR_CONNECTION_REFUSED and
            // zero bytes transferred, because the bundle had been built without
            // VITE_API_URL and was calling http://localhost:8080/api.
            if (!error.response) {
                console.error(
                    '[auth] No response from the API — this is a connectivity problem, not credentials.',
                    {
                        baseURL: error.config?.baseURL,
                        url: error.config?.url,
                        code: error.code,
                        message: error.message,
                    }
                );
            }

            const message = error.response?.data?.message
                || error.response?.data?.error
                || (error.response
                    ? `Login failed (HTTP ${error.response.status}).`
                    : error.code === 'ECONNABORTED'
                        ? 'The server took too long to respond. Please try again.'
                        : 'Cannot reach the server.');
            return rejectWithValue(message);
        }
    }
);

/**
 * Register with email and password (Step 1 - creates user account)
 * Does NOT auto-login. Returns success so UI can show confirmation dialog.
 */
export const registerUser = createAsyncThunk(
    'auth/registerUser',
    async ({ email, password }, { rejectWithValue }) => {
        try {
            const data = await registerUserApi(email, password);
            // Don't store tokens or auto-login.
            // User must login separately after registration.
            return {
                registeredEmail: email,
                message: data.message || 'Registration successful',
            };
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Registration failed. Please try again.';
            return rejectWithValue(message);
        }
    }
);

/**
 * Reset password while holding a PASSWORD_RESET_REQUIRED scoped token.
 *
 * The restricted token IS the authorisation for this call — the axios
 * interceptor will attach it automatically. On success we deliberately
 * clear auth so the user is forced to log in again with the new password.
 * The Redux action return value carries the email so the login screen
 * can pre-fill it.
 */
export const resetPassword = createAsyncThunk(
    'auth/resetPassword',
    async ({ currentPassword, newPassword }, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const email = state.auth.user?.email || null;

            await resetPasswordApi({ currentPassword, newPassword });

            // Force re-login: drop the restricted token and any cached user.
            clearAuth();

            return { email };
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Password reset failed. Please try again.';
            return rejectWithValue(message);
        }
    }
);

/**
 * Logout user.
 *
 * Accepts an optional reason so the UI can distinguish between a
 * manual sign-out and an automatic one (inactivity, cross-tab broadcast,
 * refresh failure, etc). The reason flows through to state.auth.logoutReason
 * which the Login page can read to show an appropriate banner.
 *
 *   dispatch(logoutUser())                         // manual
 *   dispatch(logoutUser({ reason: 'inactivity' })) // auto-logout
 *   dispatch(logoutUser({ reason: 'cross_tab', skipApi: true })) // already
 *                                                  // logged out elsewhere
 */
export const logoutUser = createAsyncThunk(
    'auth/logoutUser',
    async (arg = {}, { rejectWithValue }) => {
        const { reason = 'manual', skipApi = false, skipBroadcast = false } = arg || {};
        try {
            if (!skipApi) {
                await logoutApi();
            }
        } catch (error) {
            // Logout should succeed even if API fails
            console.warn('Logout API call failed, clearing local auth anyway');
        } finally {
            clearAuth();
            if (!skipBroadcast) {
                broadcastLogout(reason);
            }
        }
        return { reason };
    }
);

/**
 * Initialize auth state from stored tokens on app load
 */
export const initializeAuth = createAsyncThunk(
    'auth/initializeAuth',
    async (_, { rejectWithValue }) => {
        try {
            const accessToken = getAccessToken();

            if (!accessToken || isTokenExpired(accessToken)) {
                clearAuth();
                return { isAuthenticated: false, user: null };
            }

            const user = parseToken(accessToken);
            if (!user) {
                clearAuth();
                return { isAuthenticated: false, user: null };
            }

            return {
                isAuthenticated: true,
                user,
                passwordResetRequired: user.passwordResetRequired === true,
            };
        } catch (error) {
            clearAuth();
            return rejectWithValue('Failed to initialize auth');
        }
    }
);

// ============================================
// Slice
// ============================================

const initialState = {
    user: null,
    isAuthenticated: false,
    isLoading: false,
    isInitialized: false,
    error: null,
    registrationComplete: false,
    organisationProvisioned: false,
    registrationSuccess: false,
    registeredEmail: null,

    // Forced password reset flow
    passwordResetRequired: false,
    passwordResetReason: null,
    passwordResetMessage: null,
    passwordResetSuccess: false,
    passwordResetEmail: null,

    // Why the most recent logout happened. 'manual' | 'inactivity' |
    // 'cross_tab' | 'refresh_failed' | null. Login page reads this to
    // show an inactivity banner when appropriate.
    logoutReason: null,
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        logout: (state) => {
            state.user = null;
            state.isAuthenticated = false;
            state.isLoading = false;
            state.error = null;
            state.registrationComplete = false;
            state.organisationProvisioned = false;
            state.passwordResetRequired = false;
            state.passwordResetReason = null;
            state.passwordResetMessage = null;
        },
        clearError: (state) => {
            state.error = null;
        },
        clearLogoutReason: (state) => {
            state.logoutReason = null;
        },
        clearRegistrationSuccess: (state) => {
            state.registrationSuccess = false;
            state.registeredEmail = null;
        },
        clearPasswordResetSuccess: (state) => {
            state.passwordResetSuccess = false;
            state.passwordResetEmail = null;
        },
        updateRegistrationStatus: (state, action) => {
            state.registrationComplete = action.payload;
            if (state.user) {
                state.user.registrationComplete = action.payload;
            }
        },
        updateOrganisationStatus: (state, action) => {
            state.organisationProvisioned = action.payload;
            if (state.user) {
                state.user.organisationProvisioned = action.payload;
            }
        },
        setUserData: (state, action) => {
            state.user = { ...state.user, ...action.payload };
        },
    },
    extraReducers: (builder) => {
        // ---- Login ----
        builder.addCase(loginUser.pending, (state) => {
            state.isLoading = true;
            state.error = null;
            state.passwordResetSuccess = false;
        });
        builder.addCase(loginUser.fulfilled, (state, action) => {
            state.isLoading = false;
            state.isAuthenticated = true;
            state.user = action.payload.user;
            state.registrationComplete = action.payload.user?.registrationComplete || false;
            state.organisationProvisioned = action.payload.user?.organisationProvisioned || false;
            state.passwordResetRequired = action.payload.passwordResetRequired === true;
            state.passwordResetReason = action.payload.passwordResetReason || null;
            state.passwordResetMessage = action.payload.passwordResetMessage || null;
            state.error = null;
            state.logoutReason = null;
        });
        builder.addCase(loginUser.rejected, (state, action) => {
            state.isLoading = false;
            state.isAuthenticated = false;
            state.error = action.payload || 'Login failed';
        });

        // ---- Register ----
        builder.addCase(registerUser.pending, (state) => {
            state.isLoading = true;
            state.error = null;
            state.registrationSuccess = false;
            state.registeredEmail = null;
        });
        builder.addCase(registerUser.fulfilled, (state, action) => {
            state.isLoading = false;
            state.error = null;
            state.registrationSuccess = true;
            state.registeredEmail = action.payload.registeredEmail || null;
            // Do NOT set isAuthenticated — user must login after registration
        });
        builder.addCase(registerUser.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload || 'Registration failed';
            state.registrationSuccess = false;
        });

        // ---- Reset password ----
        builder.addCase(resetPassword.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        });
        builder.addCase(resetPassword.fulfilled, (state, action) => {
            // Success: wipe auth, flag for Login page to show success banner.
            state.isLoading = false;
            state.user = null;
            state.isAuthenticated = false;
            state.registrationComplete = false;
            state.organisationProvisioned = false;
            state.passwordResetRequired = false;
            state.passwordResetReason = null;
            state.passwordResetMessage = null;
            state.passwordResetSuccess = true;
            state.passwordResetEmail = action.payload?.email || null;
            state.error = null;
        });
        builder.addCase(resetPassword.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload || 'Password reset failed';
        });

        // ---- Logout ----
        builder.addCase(logoutUser.fulfilled, (state, action) => {
            state.user = null;
            state.isAuthenticated = false;
            state.registrationComplete = false;
            state.organisationProvisioned = false;
            state.passwordResetRequired = false;
            state.passwordResetReason = null;
            state.passwordResetMessage = null;
            state.logoutReason = action.payload?.reason || 'manual';
        });

        // ---- Initialize ----
        builder.addCase(initializeAuth.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(initializeAuth.fulfilled, (state, action) => {
            state.isLoading = false;
            state.isInitialized = true;
            state.isAuthenticated = action.payload.isAuthenticated;
            state.user = action.payload.user;
            state.registrationComplete = action.payload.user?.registrationComplete || false;
            state.organisationProvisioned = action.payload.user?.organisationProvisioned || false;
            state.passwordResetRequired = action.payload.passwordResetRequired === true;
        });
        builder.addCase(initializeAuth.rejected, (state) => {
            state.isLoading = false;
            state.isInitialized = true;
            state.isAuthenticated = false;
            state.user = null;
        });
    },
});

// ============================================
// Actions
// ============================================

export const {
    logout,
    clearError,
    clearLogoutReason,
    clearRegistrationSuccess,
    clearPasswordResetSuccess,
    updateRegistrationStatus,
    updateOrganisationStatus,
    setUserData,
} = authSlice.actions;

// ============================================
// Selectors
// ============================================

export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectUser = (state) => state.auth.user;
export const selectIsLoading = (state) => state.auth.isLoading;
export const selectAuthError = (state) => state.auth.error;
export const selectIsInitialized = (state) => state.auth.isInitialized;
export const selectRegistrationComplete = (state) => state.auth.registrationComplete;
export const selectOrganisationProvisioned = (state) => state.auth.organisationProvisioned;
export const selectRegistrationSuccess = (state) => state.auth.registrationSuccess;
export const selectRegisteredEmail = (state) => state.auth.registeredEmail;
export const selectLogoutReason = (state) => state.auth.logoutReason;

// Password reset selectors
export const selectPasswordResetRequired = (state) => state.auth.passwordResetRequired;
export const selectPasswordResetReason = (state) => state.auth.passwordResetReason;
export const selectPasswordResetMessage = (state) => state.auth.passwordResetMessage;
export const selectPasswordResetSuccess = (state) => state.auth.passwordResetSuccess;
export const selectPasswordResetEmail = (state) => state.auth.passwordResetEmail;

export default authSlice.reducer;